import _, { isFinite, isString } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  CreatePortal,
  CustomerPortalApi,
  CustomerPortalRequestParams,
} from 'api/customer-portal/api';
import {
  CustomerPortalSettingsRequestAndResponse,
  CustomerPortalSettingsApi,
  CustomerPortalSettingsAlgorithm,
  CustomerPortalSettingsMechanism,
  CustomerPortalInviteMethod,
} from 'api/customer-portal/settings-api';
import { CustomerPortal } from 'api/customer-portal/types';
import {
  AddUsersFromShareUrlRequest,
  Project,
  ProjectAPI,
  UpdateAllowedDomainsForProject,
} from 'api/project';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';
import { getDynamicConditionFields } from 'data/templates/projects/utils';
import { useIsCustomerPortalEnabled } from 'hooks/use-feature';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { db } from 'workers/db';

export const useGetAllCustomerPortalsLight = () => {
  return useQuery('useGetAllCustomerPortalsLight', () =>
    CustomerPortalApi.getCustomerPortalsInAccount(),
  );
};

export const useGetCustomerPortalById = (portalId: number) => {
  return useQuery(
    ['useGetCustomerPortalById', portalId],
    () => CustomerPortalApi.getCustomerPortalById(portalId),
    { enabled: _.isFinite(portalId) },
  );
};

export const useGetCustomerPortalByIdIDB = (portalId: number) => {
  return useQuery(
    ['useGetCustomerPortalByIdIDB', portalId],
    async () => {
      const arr = await db.customerPortal
        .where({ customerPortalId: portalId })
        .toArray();

      return JSON.parse(JSON.stringify(arr?.[0])) as CustomerPortal;
    },
    { enabled: _.isFinite(portalId) },
  );
};

export const useGetCustomerPortalByIdFromIDB = (portalId: number) => {
  const q = useGetCustomerPortalById(portalId);
  const i = useGetCustomerPortalByIdIDB(portalId);

  const iDate = moment(i?.data?.updatedAt);
  const qDate = moment(q?.data?.updatedAt);

  if (i?.isLoading && q?.isLoading) {
    return { query: i, isDirty: false };
  }

  // If the portal is not available in IDB
  if (!i?.data) {
    return { query: q, isDirty: false };
  }
  // In some case the CP is deleted
  if (!q?.data) {
    return { query: q, isDirty: false };
  }

  const isAfter = iDate.isAfter(qDate);

  if (isAfter) {
    return { query: i, isDirty: true };
  }

  return { query: q, isDirty: false };
};

export const useCreateCustomerPortal = () => {
  return useMutation((p: CreatePortal) =>
    CustomerPortalApi.createCustomerPortal(p),
  );
};

export const useUpdateCustomerPortal = () => {
  return useMutation(
    (p: { portalId: number; payload: Partial<CreatePortal> }) => {
      return CustomerPortalApi.updateCustomerPortal(p.portalId, p.payload);
    },
  );
};

export const useDeleteCustomerPortal = () => {
  return useMutation((p: number) =>
    CustomerPortalApi.deleteCustomerPortalById(p),
  );
};

export const useGetCustomerPortalForPreview = ({
  portalId,
  projectId,
}: {
  portalId: number;
  projectId: number;
}) => {
  const query = useQuery(
    ['useGetCustomerPortalForPreview', projectId, portalId],
    () =>
      CustomerPortalApi.getCustomerPortalForProjectForPreview({
        projectId,
        portalId,
      }),
  );

  return query;
};

export const useGetPortalForProject = (projectId: number) => {
  return useQuery(['useGetPortalForProject', projectId], () =>
    CustomerPortalApi.getPortalForProject(projectId),
  );
};

export const useGetInputFieldsForWidget = (
  payload?: CustomerPortalRequestParams & { widgetUuid: string },
) => {
  return useQuery(
    ['widget-input-fields', payload?.widgetType, payload?.widgetUuid],
    () => (payload ? CustomerPortalApi.getInputFields(payload) : undefined),
    {
      enabled: !!payload,
    },
  );
};

export const useGetCustomerPortalSettings = () => {
  return useQuery('customer-portal-settings', () =>
    CustomerPortalSettingsApi.getCustomerPortalsSettingsForAccount(),
  );
};

export const useCanInvite = () => {
  const { data } = useGetCustomerPortalSettings();
  const isCpEnabled = useIsCustomerPortalEnabled();

  const canInvite = useMemo(() => {
    if (!isCpEnabled) {
      return true;
    }

    if (
      data?.customerPortalSettings?.inviteMethod ===
      CustomerPortalInviteMethod.AnyoneWithTheLink
    ) {
      return true;
    }

    if (
      data?.customerPortalSettings?.inviteMethod ===
      CustomerPortalInviteMethod.EmailsFromCustomerDomainsAndAdditionalDomains
    ) {
      return data?.customerPortalSettings?.inviteMethodMeta
        ?.userWithPermissionCanInviteCustomers;
    }

    return false;
  }, [
    data?.customerPortalSettings?.inviteMethod,
    data?.customerPortalSettings?.inviteMethodMeta
      ?.userWithPermissionCanInviteCustomers,
    isCpEnabled,
  ]);

  return canInvite;
};

export const useUpdateCustomerPortalSettings = () => {
  const { refetch } = useGetCustomerPortalSettings();

  return useMutation(
    (p: CustomerPortalSettingsRequestAndResponse) =>
      CustomerPortalSettingsApi.updateCustomerPortalsSettingsForAccount(p),
    {
      onSuccess: () => {
        refetch();
      },
    },
  );
};

export const useGetDynamicConditionFieldsUsedInPortal = (
  payload: CustomerPortalRequestParams,
) => {
  return useQuery(
    [
      'useGetDynamicConditionFieldsUsedInPortal',
      payload?.portal?.customerPortalId,
    ],
    async () => {
      const d = await CustomerPortalApi.getDynamicConditionFieldsUsedInPortal(
        payload,
      );

      if (d) {
        const map = d?.reduce?.((acc, z) => {
          if (z?.fieldType === FieldTypes.MultiSelect) {
            if (isString(z?.fieldValue)) {
              z.fieldValue = z?.fieldValue
                ?.split?.(',')
                ?.map?.(Number)
                ?.filter(isFinite);
            }
          }
          acc[z?.fieldName] = z?.fieldValue;

          return acc;
        }, {});

        const fromDb = await db.liveCustomerPortal
          .where('id')
          .equals('new')
          .first();

        if (fromDb) {
          await db.liveCustomerPortal.update('new', {
            currentPage: fromDb?.currentPage,
            portal: fromDb?.portal,
            projectId: fromDb?.projectId,
            id: 'new',
            contextValues: map,
          });
        } else {
          await db.liveCustomerPortal.put({
            currentPage: payload?.portal?.pages?.filter(
              (p) => p.pageType === 'HOME',
            )?.[0]?.pageReferenceId as string,
            portal: payload?.portal as CustomerPortal,
            projectId: payload?.projectId ?? -1,
            id: 'new',
            contextValues: map,
          });
        }

        const iframe = document.getElementById(
          'cp-live-preview',
        ) as HTMLIFrameElement;

        iframe?.contentWindow?.postMessage(
          {
            type: 'cp-conditions-update',
            payload: map,
          },
          '*',
        );
      }

      return d;
    },
    {},
  );
};

export const useGetLivePreview = () => {
  return useQuery(
    ['useGetCustomerPortalForPreviewInBuilder, live'],
    async () => {
      const x = await db.liveCustomerPortal.where('id').equals('new').first();

      const resp = await CustomerPortalApi.getCustomerPortalForPreview({
        portal: x?.portal,
        contextValues: x?.contextValues,
        projectId: x?.projectId,
      });

      return {
        ...(resp ?? {}),
        currentPage: x?.currentPage,
      } as CustomerPortal & {
        currentPage: string;
      };
    },
    {
      refetchOnMount: true,
    },
  );
};

export const useGetCustomerPortalSettingsAlgorithmOptions = () => {
  const translate = useTranslate();

  return Object.values(CustomerPortalSettingsAlgorithm).map((key) => ({
    value: key,
    label: translate(
      `customer_portal_settings.algorithm.${key}.label` as TranslationKey,
    ),
  }));
};

export const useGetCustomerPortalMechanismOptions = () => {
  const translate = useTranslate();

  return Object.values(CustomerPortalSettingsMechanism).map((key) => ({
    value: key,
    label: translate(
      `customer_portal_settings.mechanism.${key}.label` as TranslationKey,
    ),
    hint: translate(
      `customer_portal_settings.mechanism.${key}.description` as TranslationKey,
    ),
  }));
};

export const useGetAllowedWidgetsForPageType = (
  payload: CustomerPortalRequestParams,
) => {
  return useQuery(
    [
      'useGetAllowedWidgetsForPageType',
      payload?.portal?.customerPortalId,
      payload?.pageType,
    ],
    () => CustomerPortalApi.getAllowedWidgetsForPageType(payload),
  );
};

export const useCustomerPortalProjects = (projectId: number | undefined) => {
  return useQuery(
    ['customer-portal-projects', projectId],
    async () => {
      if (!projectId) {
        return [] as Project[];
      }

      return CustomerPortalApi.getCustomerPortalProjects({ projectId });
    },
    { enabled: projectId !== undefined && projectId !== -1 },
  );
};

export const useGetPortalProjectMapping = (projectId: number) => {
  return useQuery(['useGetPortalProjectMapping', projectId], () =>
    CustomerPortalApi.getPortalProjectMapping(projectId),
  );
};

export const useUpdateAllowedDomainsForProject = () => {
  return useMutation(
    (p: { projectId: number; payload: UpdateAllowedDomainsForProject }) => {
      return ProjectAPI.updateAllowedDomains(p.projectId, p?.payload);
    },
  );
};

export const useAddUsersFromShareUrl = () => {
  return useMutation((p: AddUsersFromShareUrlRequest) =>
    ProjectAPI.addUsersFromShareUrl(p),
  );
};

export const useGetDynamicConditionFields = () => {
  return useQuery('dynamicConditionFields', () =>
    CustomerPortalApi.getDynamicConditionFields(),
  );
};

export const useDynamicConditionFilterableFieldsForCustomerPortal = () => {
  const { data: fields = [] } = useGetDynamicConditionFields();

  const filterableFields = useMemo(
    () =>
      getDynamicConditionFields(
        fields.map((f) => ({ ...(f ?? {}), customField: false })),
        false,
      ),
    [fields],
  );

  return filterableFields;
};

export const useGetSmartfillFieldsForCustomerPortal = (
  params: CustomerPortalRequestParams | undefined,
) => {
  return useQuery(['useGetSmartfillFieldsForCustomerPortal', params], () =>
    params
      ? CustomerPortalApi.getVariableFields(params)
      : Promise.resolve({
          variableFields: [],
          nestedFields: [],
        }),
  );
};

export const useGetProjectsPortalCount = (portalId: number | null) => {
  return useQuery(['useGetProjectsPortalCount', portalId], () =>
    !portalId ? undefined : CustomerPortalApi.getProjectsCount(portalId),
  );
};
